import { faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { CGSaleLandAndBuildingDetailsForm } from ".";
import { IFormITR1, ISaleLandAndBuildingForm } from "../../../config/interfaces";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const CGSaleLandAndBuildingDetails: React.FC = () => {
  const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<ISaleLandAndBuildingForm | null>(null);
  const [activeItem, setActiveItem] = useState<ISaleLandAndBuildingForm | null>(null);

  // used in both edit and delete cases
  const [activeItemIdx, setActiveItemIdx] = useState<string>("");

  const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
  const SaleLandAndBuildingDestails = cloneDeep(values.IncomeDetails?.SaleLandAndBuildings || []);

  const handleDeleteInterestItem = async () => {
    SaleLandAndBuildingDestails.splice(Number(activeItemIdx), 1);
    setFieldValue("IncomeDetails.SaleLandAndBuildings", SaleLandAndBuildingDestails);
    setShowDeleteModal(() => null);
    setActiveItem(() => null);
    setActiveItemIdx(() => "");
    handleTotalCalculation();
  };

  const handleTotalCalculation = () => {
    const LandBuildingGains =
      SaleLandAndBuildingDestails?.reduce((acc, curr) => {
        const value = parseInt(curr.CapitalGains);
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const CapitalGains = {
      ...values.CapitalGains,
      TotalLandBuildingGain: LandBuildingGains
    };

    const TotalCapitalGains = Object.values(CapitalGains)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const DATA = {
      CapitalGains: CapitalGains,
      TotalCapitalGains: TotalCapitalGains
    };

    const _TotalSplRateTax =
      SaleLandAndBuildingDestails?.reduce((acc, curr) => {
        const value = curr.SplRateTax;
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const _TotalNrlRateIncome =
      SaleLandAndBuildingDestails?.reduce((acc, curr) => {
        const value = curr.NrlRateIncome;
        return acc + (isNaN(value) ? 0 : value);
      }, 0) || 0;

    const CapitalGainsSplRateTax = {
      ...values.CapitalGainsSplRateTax,
      LandSplRateTax: _TotalSplRateTax
    };

    const CapitalGainsNrlRateIncome = {
      ...values.CapitalGainsNrlRateIncome,
      LandNrlRateIncome: _TotalNrlRateIncome
    };

    const TotalCGSplRtTax = Object.values(CapitalGainsSplRateTax)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const TotalCGNrlRtInc = Object.values(CapitalGainsNrlRateIncome)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const SPL_DATA = {
      CapitalGainsSplRateTax: { ...CapitalGainsSplRateTax },
      CapitalGainsNrlRateIncome: { ...CapitalGainsNrlRateIncome },
      TotalSplRateTax: TotalCGSplRtTax,
      TotalNrlRateIncome: TotalCGNrlRtInc
    };

    setValues({ ...cloneDeep(values), ...DATA, ...SPL_DATA });
    setFieldValue("IncomeDetails.SaleLandAndBuildings", SaleLandAndBuildingDestails);
  };

  return (
    <>
      <section className="mb-5">
        <div className="section-title-wrap">
          <h5>Sale of Land and Building</h5>
          <PreventEditComponent>
            <Button variant="secondary" size="sm" onClick={() => setShowAddFormCanvas(() => true)}>
              <FASvgIcon icon={faPlus} />
              Add
            </Button>
          </PreventEditComponent>
        </div>

        <dl className="eds-list eds-dl eds-inline-between">
          {SaleLandAndBuildingDestails.map((SaleLandAndBuilding, index) => {
            return (
              <div key={`any-asset-${index}`} className="eds-list--item">
                <dt>{SaleLandAndBuilding.DescriptionAssetSold}</dt>
                <dd>
                  <span>
                    <PriceDisplay price={SaleLandAndBuilding?.CapitalGains} />
                  </span>
                  <Button
                    variant="link"
                    onClick={() => {
                      setShowAddFormCanvas(() => true);
                      setActiveItem(() => SaleLandAndBuilding);
                      setActiveItemIdx(() => String(index));
                    }}
                  >
                    View
                    <PreventEditComponent>
                      <>/Edit</>
                    </PreventEditComponent>
                  </Button>
                  <PreventEditComponent>
                    <Button
                      variant="transparent"
                      className="icon-only"
                      onClick={() => {
                        setShowDeleteModal(() => SaleLandAndBuilding);
                        setActiveItemIdx(() => String(index));
                      }}
                    >
                      <FASvgIcon icon={faTrash} />
                    </Button>
                  </PreventEditComponent>
                </dd>
              </div>
            );
          })}
        </dl>
      </section>
      <CGSaleLandAndBuildingDetailsForm
        showCanvas={showAddFormCanvas}
        handleCloseBtnPress={(saleLandAndBuildingData) => {
          if (saleLandAndBuildingData) {
            if (activeItem) {
              SaleLandAndBuildingDestails.splice(Number(activeItemIdx), 1, saleLandAndBuildingData);
            } else {
              SaleLandAndBuildingDestails.push(saleLandAndBuildingData);
            }
            handleTotalCalculation();
          }
          setShowAddFormCanvas(() => false);
          setActiveItem(() => null);
          setActiveItemIdx(() => "");
        }}
        formValues={activeItem}
      />

      <ConfirmationModal
        showModal={!!showDeleteModal}
        iconColorClass="text-danger"
        title="Are you sure?"
        message={<p>You want to delete this</p>}
        onYesBtnClick={() => handleDeleteInterestItem()}
        onNoBtnClick={() => setShowDeleteModal(() => null)}
        icon={faTrashAlt}
      />
    </>
  );
};

export default CGSaleLandAndBuildingDetails;
